import { RefObject, useCallback, useEffect, useState } from 'react'

/** Matches airport selector popper stacking in the SRP search modifier. */
export const SEARCH_MODIFIER_POPPER_Z_INDEX = 1500

const DEFAULT_OFFSET: [number, number] = [0, 6]

export function resolveSearchModifierPopperAnchor(
    triggerEl: HTMLElement | null,
    attachToSearchContainer: boolean
): { anchorEl: HTMLElement | null; offset: [number, number] } {
    if (!triggerEl) {
        return { anchorEl: null, offset: DEFAULT_OFFSET }
    }

    if (attachToSearchContainer) {
        const container = triggerEl.closest('[data-search-container]') as HTMLElement | null
        if (container) {
            const containerRect = container.getBoundingClientRect()
            const triggerRect = triggerEl.getBoundingClientRect()
            return {
                anchorEl: container,
                offset: [triggerRect.left - containerRect.left, 0]
            }
        }
    }

    return { anchorEl: triggerEl, offset: DEFAULT_OFFSET }
}

export function useSearchContainerPopperAnchor(
    active: boolean,
    triggerRef: RefObject<HTMLElement | null>,
    attachToSearchContainer: boolean
) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const [offset, setOffset] = useState<[number, number]>(DEFAULT_OFFSET)

    const syncAnchor = useCallback(() => {
        const { anchorEl: el, offset: nextOffset } = resolveSearchModifierPopperAnchor(
            triggerRef.current,
            attachToSearchContainer
        )
        setAnchorEl(el)
        setOffset(nextOffset)
    }, [attachToSearchContainer, triggerRef])

    useEffect(() => {
        if (active) {
            syncAnchor()
        } else {
            setAnchorEl(null)
        }
    }, [active, syncAnchor])

    return {
        anchorEl,
        offset,
        popperOpen: active && Boolean(anchorEl)
    }
}
